import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GavelIcon from "@mui/icons-material/Gavel";
import { APP_COLORS as C } from "../../theme/colors";

const STRIP = { approved: "#2e7d32", rejected: "#d32f2f" };

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const person = (name, username, id) =>
  `${name || `User #${id}`}${username ? ` @${username}` : ""}`;

/**
 * Admin-only history: every sketch that has been decided (approved or
 * rejected), latest decision first, with who requested and who decided.
 */
export default function HistoryList({
  items,
  loading,
  activeId,
  filter,
  page,
  pages,
  total,
  pageSize,
  onFilter,
  onView,
  onPage,
  onRefresh,
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: C.primary, lineHeight: 1.2 }}>
            History
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total} decided sketch{total === 1 ? "" : "es"} · latest first
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 2, pb: 1.25 }}>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
          sx={{ fontSize: 13, bgcolor: "#fff" }}
        >
          <MenuItem value="">Approved &amp; rejected</MenuItem>
          <MenuItem value="approved">Approved only</MenuItem>
          <MenuItem value="rejected">Rejected only</MenuItem>
        </Select>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 1, position: "relative" }}>
        {loading && (
          <Box sx={{ position: "absolute", top: 4, right: 16 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ textAlign: "center", color: C.textMuted, px: 2, py: 5 }}>
            <HistoryIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {filter ? `No ${filter} sketches yet` : "No decisions yet"}
            </Typography>
            <Typography variant="caption">
              Approved and rejected sketches appear here.
            </Typography>
          </Box>
        )}

        {items.map((s) => {
          const active = s.sketch_id === activeId;
          const approved = s.status === "approved";
          return (
            <Box
              key={s.sketch_id}
              sx={{
                mb: 1,
                p: 1.25,
                pl: 1.75,
                borderRadius: 2,
                bgcolor: active ? C.primaryLight : C.surface,
                border: `1px solid ${active ? C.primaryMid : C.border}`,
                borderLeft: `4px solid ${STRIP[s.status] || C.border}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1, color: C.text }} noWrap>
                  {s.name}
                </Typography>
                <Chip
                  size="small"
                  label={s.status}
                  color={approved ? "success" : "error"}
                  sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.75 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: C.primaryMid }}>
                  {initials(s.created_by_name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.1 }}>
                    Requested by
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                    {person(s.created_by_name, s.created_by_username, s.created_by)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, color: C.textMuted }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    {s.feature_count} feature{s.feature_count === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    Submitted {formatDate(s.submitted_at)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5, color: approved ? "#2e7d32" : "#c62828" }}>
                <GavelIcon sx={{ fontSize: 14, mt: "2px" }} />
                <Typography variant="caption">
                  {approved ? "Approved" : "Rejected"} by{" "}
                  <b>{person(s.reviewed_by_name, s.reviewed_by_username, s.reviewed_by)}</b>
                  {s.reviewed_at ? ` · ${formatDate(s.reviewed_at)}` : ""}
                </Typography>
              </Box>

              {s.review_remarks && (
                <Box sx={{ mt: 0.75, px: 1, py: 0.5, borderRadius: 1, bgcolor: approved ? "#e8f5e9" : "#ffebee" }}>
                  <Typography variant="caption" sx={{ color: approved ? "#1b5e20" : "#c62828" }}>
                    <b>Remarks:</b> {s.review_remarks}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => onView(s)}>
                  View
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      {pages > 0 && (
        <Box
          sx={{
            borderTop: `1px solid ${C.border}`,
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#fafbfc",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {from}–{to} of {total}
          </Typography>
          <Pagination size="small" count={pages} page={page} siblingCount={0} boundaryCount={1} color="primary" onChange={(_, p) => onPage(p)} />
        </Box>
      )}
    </Box>
  );
}
